/**
 * Collision Store
 * Zustand state management for collision detection
 */

import { create } from 'zustand'
import type { CollisionDetails, SafetyMarginsConfig, CollisionResult } from '@/types/collision'
import { DEFAULT_SAFETY_MARGINS } from '@/types/collision'
import { ENABLE_COLLISION_DEBUG } from '@/constants/collision'

/**
 * Collision state interface
 */
interface CollisionState {
  /** Set of aircraft allocation IDs that are currently colliding */
  collidingAircraftIds: Set<string>

  /** Detailed collision info per aircraft */
  collisionDetails: Map<string, CollisionDetails>

  /** Safety margins configuration (from location or defaults) */
  safetyMargins: SafetyMarginsConfig

  /** Last collision check duration in milliseconds */
  lastCheckDuration: number

  /** Whether collision detection is currently enabled */
  isEnabled: boolean

  /** Whether collision detection is currently running */
  isChecking: boolean
}

/**
 * Collision actions interface
 */
interface CollisionActions {
  /** Set collisions from a CollisionResult */
  setCollisions: (result: CollisionResult) => void

  /** Add a single collision with details */
  addCollision: (details: CollisionDetails) => void

  /** Remove a collision for a specific aircraft */
  removeCollision: (allocationId: string) => void

  /** Clear all collisions */
  clearAllCollisions: () => void

  /** Update safety margins */
  setSafetyMargins: (margins: Partial<SafetyMarginsConfig>) => void

  /** Check if a specific aircraft is colliding */
  isColliding: (allocationId: string) => boolean

  /** Get collision details for a specific aircraft */
  getCollisionDetails: (allocationId: string) => CollisionDetails | undefined

  /** Enable/disable collision detection */
  setEnabled: (enabled: boolean) => void

  /** Set checking state (internal) */
  setChecking: (checking: boolean) => void

  /** Merge collision results (for partial checks during drag) */
  mergeCollisions: (result: CollisionResult, movingAircraftId?: string) => void
}

/**
 * Collision store type
 */
export type CollisionStore = CollisionState & CollisionActions

/**
 * Collision store
 */
export const useCollisionStore = create<CollisionStore>((set, get) => ({
  // Initial state
  collidingAircraftIds: new Set<string>(),
  collisionDetails: new Map<string, CollisionDetails>(),
  safetyMargins: DEFAULT_SAFETY_MARGINS,
  lastCheckDuration: 0,
  isEnabled: true,
  isChecking: false,

  // Actions
  setCollisions: (result: CollisionResult) => {
    const newDetails = new Map<string, CollisionDetails>()
    const now = Date.now()

    for (const id of result.collisions) {
      const obstacleInfo = result.obstacleCollisions?.get(id)
      const wallInfo = result.wallCollisions?.get(id)

      if (obstacleInfo) {
        newDetails.set(id, {
          allocationId: id,
          collisionType: 'obstacle',
          obstacleId: obstacleInfo.obstacleId,
          obstacleName: obstacleInfo.obstacleName,
          detectedAt: now,
        })
      } else if (wallInfo) {
        newDetails.set(id, {
          allocationId: id,
          collisionType: wallInfo.isDoor ? 'door' : 'wall',
          wallSide: wallInfo.side,
          areaId: wallInfo.areaId,
          unitId: wallInfo.unitId,
          detectedAt: now,
        })
      } else {
        const partners = Array.from(result.collisions).filter((o) => o !== id)
        newDetails.set(id, {
          allocationId: id,
          collisionType: 'aircraft',
          collidingAircraftIds: partners,
          detectedAt: now,
        })
      }
    }

    set({
      collidingAircraftIds: result.collisions,
      collisionDetails: newDetails,
      lastCheckDuration: result.checkDurationMs,
    })
  },

  addCollision: (details: CollisionDetails) => {
    const { collidingAircraftIds, collisionDetails } = get()
    const newCollisions = new Set(collidingAircraftIds)
    newCollisions.add(details.allocationId)

    const newDetails = new Map(collisionDetails)
    newDetails.set(details.allocationId, details)

    set({
      collidingAircraftIds: newCollisions,
      collisionDetails: newDetails,
    })
  },

  removeCollision: (allocationId: string) => {
    const { collidingAircraftIds, collisionDetails } = get()
    const newCollisions = new Set(collidingAircraftIds)
    newCollisions.delete(allocationId)

    const newDetails = new Map(collisionDetails)
    newDetails.delete(allocationId)

    set({
      collidingAircraftIds: newCollisions,
      collisionDetails: newDetails,
    })
  },

  clearAllCollisions: () => {
    set({
      collidingAircraftIds: new Set<string>(),
      collisionDetails: new Map<string, CollisionDetails>(),
    })
  },

  setSafetyMargins: (margins: Partial<SafetyMarginsConfig>) => {
    const { safetyMargins } = get()
    set({
      safetyMargins: { ...safetyMargins, ...margins },
    })
  },

  isColliding: (allocationId: string) => {
    return get().collidingAircraftIds.has(allocationId)
  },

  getCollisionDetails: (allocationId: string) => {
    return get().collisionDetails.get(allocationId)
  },

  setEnabled: (enabled: boolean) => {
    set({ isEnabled: enabled })
    if (!enabled) {
      // Clear collisions when disabled
      set({
        collidingAircraftIds: new Set<string>(),
        collisionDetails: new Map<string, CollisionDetails>(),
      })
    }
  },

  setChecking: (checking: boolean) => {
    set({ isChecking: checking })
  },

  mergeCollisions: (result: CollisionResult, movingAircraftId?: string) => {
    const { collidingAircraftIds, collisionDetails } = get()

    if (ENABLE_COLLISION_DEBUG) {
      console.log('🔀 mergeCollisions called:', {
        movingAircraftId,
        checkedIds: result.checkedAircraftIds,
        newCollisions: Array.from(result.collisions),
        currentCollisions: Array.from(collidingAircraftIds),
      })
    }

    // For partial checks, we need to merge results carefully
    // KEY PRINCIPLE: Only modify collision state for aircraft DIRECTLY involved with moving aircraft
    // Do NOT clear nearby aircraft that have their own separate collisions
    const newCollisions = new Set(collidingAircraftIds)
    const newDetails = new Map(collisionDetails)

    if (movingAircraftId) {
      // Step 1: Get aircraft that were PREVIOUSLY colliding with moving aircraft
      const movingDetails = collisionDetails.get(movingAircraftId)
      const previouslyCollidingWithMoving = movingDetails?.collidingAircraftIds ?? []

      // Step 2: Clear ONLY the moving aircraft from collision set
      newCollisions.delete(movingAircraftId)
      newDetails.delete(movingAircraftId)

      // Step 3: For aircraft that WERE colliding with moving aircraft,
      // remove the moving aircraft from their collision partners list
      // BUT keep them in collisions if they still have OTHER collision partners
      for (const otherId of previouslyCollidingWithMoving) {
        const otherDetails = newDetails.get(otherId)
        if (otherDetails?.collidingAircraftIds) {
          // Remove moving aircraft from their collision partners
          const remainingPartners = otherDetails.collidingAircraftIds.filter(
            (id) => id !== movingAircraftId
          )
          if (remainingPartners.length === 0) {
            // No other collision partners - clear this aircraft
            newCollisions.delete(otherId)
            newDetails.delete(otherId)
            if (ENABLE_COLLISION_DEBUG) {
              console.log(`🔗 Cleared ${otherId} (was only colliding with ${movingAircraftId})`)
            }
          } else {
            // Still has other collision partners - update the list
            newDetails.set(otherId, {
              ...otherDetails,
              collidingAircraftIds: remainingPartners,
            })
            if (ENABLE_COLLISION_DEBUG) {
              console.log(`🔗 Updated ${otherId}: still colliding with ${remainingPartners.join(', ')}`)
            }
          }
        }
      }
    }

    // Step 4: Add back aircraft that are NOW colliding (from this check)
    for (const id of result.collisions) {
      newCollisions.add(id)
    }

    // Step 5: Update collision details for newly colliding aircraft
    if (result.collisions.size > 0 && movingAircraftId) {
      // For partial check: moving aircraft collides with others
      const collidingArray = Array.from(result.collisions)

      for (const id of collidingArray) {
        const existingDetails = newDetails.get(id)
        const wallInfo = result.wallCollisions?.get(id)

        // Check if this is a wall/door collision
        const obstacleInfo = result.obstacleCollisions?.get(id)

        if (obstacleInfo) {
          newDetails.set(id, {
            allocationId: id,
            collisionType: 'obstacle',
            obstacleId: obstacleInfo.obstacleId,
            obstacleName: obstacleInfo.obstacleName,
            detectedAt: existingDetails?.detectedAt ?? Date.now(),
          })
        } else if (wallInfo) {
          newDetails.set(id, {
            allocationId: id,
            collisionType: wallInfo.isDoor ? 'door' : 'wall',
            wallSide: wallInfo.side,
            areaId: wallInfo.areaId,
            unitId: wallInfo.unitId,
            detectedAt: existingDetails?.detectedAt ?? Date.now(),
          })
        } else {
          // Aircraft-to-aircraft collision
          const existingPartners = existingDetails?.collidingAircraftIds ?? []

          // Determine new collision partners from this check
          const newPartnersFromCheck = collidingArray.filter((otherId) => otherId !== id)

          // Merge with existing partners (if aircraft had other collisions)
          const allPartners = [...new Set([...existingPartners, ...newPartnersFromCheck])]

          newDetails.set(id, {
            allocationId: id,
            collisionType: 'aircraft',
            collidingAircraftIds: allPartners,
            detectedAt: existingDetails?.detectedAt ?? Date.now(),
          })
        }
      }
    }

    if (ENABLE_COLLISION_DEBUG) {
      console.log('🔀 mergeCollisions result:', {
        finalCollisions: Array.from(newCollisions),
        detailsCount: newDetails.size,
      })
    }

    set({
      collidingAircraftIds: newCollisions,
      collisionDetails: newDetails,
      lastCheckDuration: result.checkDurationMs,
    })
  },
}))

// Register on window for dev tools access
if (typeof window !== 'undefined') {
  ;(window as unknown as Record<string, unknown>).__collisionStore = useCollisionStore
}
